// Marketplace — Partner Enablement drawer (Marketplace/Partner initiative, Phase 1, Inc 5b).
// The stepped flow that enforces CORE RULE A: an employer must APPROVE the PARTNER (organization
// level) and record data-sharing CONSENT before enabling any of that partner's products. Matches the
// approved enablement design. Two backend actions behind the steps: approvePartner (the partner-level
// gate + consent) then requestAccess (enable this product → partner review). "Configure" (eligible
// groups, caps, dates, deduction authorization) happens after the partner accepts — noted here, wired
// by the saveProgram surface in a follow-up.
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  ShieldCheck, Lock, ArrowRight, ArrowLeft, CheckCircle2, Building2, Users, FileCheck2,
} from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from '@/features/shared/components/ui/sheet';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Checkbox } from '@/features/shared/components/ui/checkbox';
import { creditApi, type MarketplaceProduct } from '@/features/credit/api/creditApi';

// The signals the employer consents to share — recorded on the relationship's consent_scope so the
// grant is auditable and specific (never a blanket "share everything").
const CONSENT_SCOPE = {
  employer: ['company_name', 'tin', 'employee_group_size'],
  per_applicant: ['name', 'employee_number', 'kaziscore', 'net_pay_band', 'employment_status', 'salary_account_ref'],
  purpose: 'partner_eligibility_decision',
} as const;

const STEP_LABELS = ['Approve the partner', 'Consent & data-sharing', 'Done'];

export default function PartnerEnablementDrawer({
  open, onOpenChange, product, onCompleted,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  product: MarketplaceProduct;
  onCompleted: () => void;
}) {
  const [step, setStep] = useState(0);
  const [consented, setConsented] = useState(false);
  const [busy, setBusy] = useState(false);
  const partner = product.partnerName || 'the partner';

  const reset = () => { setStep(0); setConsented(false); setBusy(false); };
  const close = (o: boolean) => { if (!o) reset(); onOpenChange(o); };

  const submit = async () => {
    if (!consented) return;
    setBusy(true);
    try {
      // 1) The partner-level gate: approve the partner + record consent.
      await creditApi.approvePartner(product.partnerId, CONSENT_SCOPE as unknown as Record<string, unknown>);
      // 2) Enable THIS product (request access → the partner reviews it). Skip if already in flight.
      if (!product.agreementStatus || ['declined', 'revoked'].includes(product.agreementStatus)) {
        await creditApi.requestAccess(product.id);
      }
      setStep(2);
      onCompleted();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not approve the partner.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={close}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b border-border px-5 py-4 text-left">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Enable a product · Step {Math.min(step + 1, STEP_LABELS.length)} of {STEP_LABELS.length}
          </p>
          <SheetTitle className="text-lg text-charcoal">{product.name}</SheetTitle>
        </SheetHeader>

        {/* Stepper rail */}
        <div className="flex items-center gap-1.5 px-5 py-3">
          {STEP_LABELS.map((label, i) => (
            <div key={label} className="flex flex-1 items-center gap-1.5">
              <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold
                ${i < step ? 'bg-success text-white' : i === step ? 'bg-primary-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                {i < step ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
              </span>
              {i < STEP_LABELS.length - 1 && <span className={`h-px flex-1 ${i < step ? 'bg-success' : 'bg-border'}`} />}
            </div>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-2">
          {step === 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-foreground">{partner}</span>
                <Badge variant="success" className="gap-1"><ShieldCheck className="h-3 w-3" /> Verified partner</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                You’re approving <b className="text-foreground">{partner}</b> as a partner for your organization. This
                unlocks their products for you to enable. The partner provides, funds and is responsible for the
                regulated product — KaziPeople governs listing, consent and audit.
              </p>
              <ul className="space-y-2.5 text-sm">
                <Point icon={<Building2 className="h-4 w-4" />}><b className="text-foreground">Provided &amp; regulated by</b> {partner}, licensed by its financial regulator.</Point>
                <Point icon={<Users className="h-4 w-4" />}>Employees only ever see products from partners <b className="text-foreground">you approve</b> and products <b className="text-foreground">you enable</b> — never before.</Point>
                <Point icon={<Lock className="h-4 w-4" />}>Nothing is shared until you consent on the next step.</Point>
              </ul>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-info-strong">
                When you approve this partner, the following is shared — and only when an employee applies:
              </p>
              <div className="rounded-lg border border-info/40 bg-info-soft p-3.5 text-sm">
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex gap-2"><ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-info-strong" /><span><b className="text-foreground">Employer:</b> company name, TIN, participating group size.</span></li>
                  <li className="flex gap-2"><ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-info-strong" /><span><b className="text-foreground">Per applying employee:</b> name, employee number, <b className="text-foreground">KaziScore</b>, verified net-pay band &amp; employment status, salary-account reference.</span></li>
                  <li className="flex gap-2"><ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-info-strong" /><span><b className="text-foreground">Purpose:</b> the partner’s eligibility decision · <b className="text-foreground">Withdraw:</b> disable any time.</span></li>
                </ul>
                <p className="mt-2.5 text-xs text-muted-foreground">No national IDs, full salary history or bank balances are shared. KaziPeople does not make the credit decision — it supplies these consented signals to help the partner decide.</p>
              </div>
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3.5 text-sm">
                <Checkbox checked={consented} onCheckedChange={(v) => setConsented(v === true)} className="mt-0.5" />
                <span className="text-foreground">
                  I’m authorized to approve this partner on behalf of my organization and I consent to sharing the
                  signals described above. This consent is recorded in our audit trail.
                </span>
              </label>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 py-4 text-center">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-success-soft text-success-strong">
                <FileCheck2 className="h-7 w-7" />
              </span>
              <div>
                <h3 className="text-base font-semibold text-charcoal">Partner approved &amp; product requested</h3>
                <p className="mx-auto mt-1.5 max-w-xs text-sm text-muted-foreground">
                  {partner} will review the request. Once accepted, an authorized approver configures eligible groups,
                  caps, effective date and payroll deduction — then it goes live for eligible employees.
                </p>
              </div>
              <p className="text-xs text-muted-foreground">Your consent is recorded in the audit trail.</p>
            </div>
          )}
        </div>

        <SheetFooter className="border-t border-border px-5 py-4">
          {step === 0 && (
            <Button className="w-full gap-1.5" onClick={() => setStep(1)}>
              Continue to consent <ArrowRight className="h-4 w-4" />
            </Button>
          )}
          {step === 1 && (
            <div className="flex w-full gap-2">
              <Button variant="outline" className="gap-1.5" onClick={() => setStep(0)} disabled={busy}>
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
              <Button className="flex-1 gap-1.5" disabled={!consented || busy} onClick={submit}>
                {busy ? 'Approving…' : <>Approve partner &amp; consent <CheckCircle2 className="h-4 w-4" /></>}
              </Button>
            </div>
          )}
          {step === 2 && (
            <Button className="w-full" onClick={() => close(false)}>Done</Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

const Point: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <li className="flex items-start gap-2.5 text-muted-foreground">
    <span className="mt-0.5 shrink-0 text-primary-500">{icon}</span>
    <span>{children}</span>
  </li>
);
